import React, { useContext, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import styled from 'styled-components';
import { userApiRequests } from 'utils/api-requests/user';
import PropertyValuationContext from 'utils/context/PropertyValuationContext';
import { v4 } from 'uuid';

import ProgressIndicator, { ProgressStep } from 'components/atom/ProgressIndicator';
import FiltersContentWrapper from 'components/common/FiltersContentWrapper';
import ValuationPdfPages from 'components/common/PdfRenderer/ValuationPdfPage';
import ResourcePopup from 'components/common/ResourcePopUp';
import { activeNotification } from 'components/functions/activeNotification';
import V2HeaderSection from 'components/V2HeaderSection';
import { captureMapImage } from 'components/valuation/captureMapImage';
import FiltersValuation from 'components/valuation/FiltersValuation';
import PriceRangeStep from 'components/valuation/PriceRangeStep';
import ValuationAnalysis from 'components/valuation/ValuationAnalysis';
import ValuationReportResult from 'components/valuation/ValuationReportResult';

const PDF_IMG_WIDTH = 595;
const PDF_MAP_DELAY = 1500;

const VALUATION_STEPS: ProgressStep[] = [
    { key: 'STARTED', label: 'Start' },
    { key: 'FETCHING_DATA', label: 'Dane' },
    { key: 'DISPATCHING_TO_VALUATION', label: 'Przygotowanie' },
    { key: 'CALCULATING_VALUATION', label: 'Obliczenia' },
    { key: 'GENERATING_AI_REPORT', label: 'Raport AI' },
    { key: 'COMPLETED', label: 'Gotowe' }
];

const Wrapper = styled.div`
    .active .accordion-card-content {
        row-gap: 10px;
    }
`;

const Container = styled.div`
    padding: 15px 20px 0;

    @media (max-width: 1100px) {
        padding: 20px 20px 0;
        margin-bottom: 20px;
    }
`;

const ResultContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    border-radius: var(--box-border-radius);
    margin-top: 40px;
    gap: 16px;
`;

const NewValuationButton = styled.div`
    cursor: pointer;
    color: var(--color-primary);
    font-weight: var(--font-weight);
    margin: 10px 0 0 15px;
    display: flex;
    font-size: 15px;
    height: 50px;
    border: 2px solid var(--color-primary);
    border-radius: 10px;
    padding: 12px;

    @media (max-width: 768px) {
        margin: -25px 0 10px 0;
        height: auto;
        align-self: flex-start;
    }
`;

const PropertyValuationPage = () => {
    const { valuationState, valuationFiltersState, loaderText, currentStatus, clearFilters, isOpenResourcePopUp, closeResourcePopUp } = useContext(PropertyValuationContext);

    const [isPrintMode, setIsPrintMode] = useState(false);
    const [mapImageUrl, setMapImageUrl] = useState<string | undefined>();
    const pdfPage1Ref = useRef<HTMLDivElement>(null);
    const pdfPage2Ref = useRef<HTMLDivElement>(null);
    const pdfPage3Ref = useRef<HTMLDivElement>(null);
    const pdfTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => {
        if (pdfTimeoutRef.current) clearTimeout(pdfTimeoutRef.current);
    }, []);

    const showPriceRange = Boolean(valuationState.priceRangeResponse);
    const hasResults = Boolean(valuationState.valuationResultV2);

    const valuationResult = valuationState.valuationResultV2;
    const locationLabel = Object.values(valuationFiltersState.savedLocations)[0]?.label;
    const subjectAddress = [valuationFiltersState.buildingNumber, locationLabel].filter((v) => v?.trim()).join(', ') || undefined;

    const handleDownloadPdf = async () => {
        const mapContainer = document.querySelector('[data-map-capture]') as HTMLElement | null;
        const capturedMapUrl = mapContainer ? await captureMapImage(mapContainer) : undefined;

        setMapImageUrl(capturedMapUrl);
        setIsPrintMode(true);

        pdfTimeoutRef.current = setTimeout(async () => {
            const page1 = pdfPage1Ref.current;
            const page2 = pdfPage2Ref.current;
            const page3 = pdfPage3Ref.current;

            if (!page1 || !page2 || !page3) {
                setIsPrintMode(false);

                return;
            }

            try {
                const [
                    canvas1,
                    canvas2,
                    canvas3
                ] = await Promise.all([
                    html2canvas(page1, { allowTaint: true, useCORS: true, scale: 1.2 }),
                    html2canvas(page2, { allowTaint: true, useCORS: true, scale: 1.2 }),
                    html2canvas(page3, { allowTaint: true, useCORS: true, scale: 1.2 })
                ]);

                const toImg = (canvas: HTMLCanvasElement) => ({
                    data: canvas.toDataURL('image/jpeg', 0.85),
                    height: Math.round(canvas.height * PDF_IMG_WIDTH / canvas.width)
                });

                const img1 = toImg(canvas1);
                const img2 = toImg(canvas2);
                const img3 = toImg(canvas3);

                const pdf = new jsPDF('p', 'pt', 'a4');

                pdf.addImage(img1.data, 'JPEG', 0, 0, PDF_IMG_WIDTH, img1.height);
                pdf.addPage();
                pdf.addImage(img2.data, 'JPEG', 0, 0, PDF_IMG_WIDTH, img2.height);
                pdf.addPage();
                pdf.addImage(img3.data, 'JPEG', 0, 0, PDF_IMG_WIDTH, img3.height);

                pdf.save(`wycena_investoro_${dayjs().format('DD_MM_YYYY')}.pdf`);
            } catch (error) {
                const errorId = v4();
                userApiRequests.logException(error as Error, errorId);
                activeNotification('Błąd generowania PDF', 'Nie udało się wygenerować pliku PDF. Spróbuj ponownie.', 'warning');
            } finally {
                setIsPrintMode(false);
            }
        }, PDF_MAP_DELAY);
    };

    const renderLoader = () => {
        if (!valuationState.isLoading) return null;

        return (
            <ProgressIndicator
                text={loaderText}
                currentStep={currentStatus || undefined}
                steps={VALUATION_STEPS}
            />
        );
    };

    return (
        <Wrapper>
            {renderLoader()}
            {isPrintMode && valuationResult &&
                <ValuationPdfPages
                    firstPageRef={pdfPage1Ref}
                    secondPageRef={pdfPage2Ref}
                    thirdPageRef={pdfPage3Ref}
                    valuationResult={valuationResult}
                    subjectAddress={subjectAddress}
                    mapImageUrl={mapImageUrl}
                />
            }
            <V2HeaderSection
                additionalElement={<NewValuationButton onClick={hasResults ? clearFilters : undefined}>Nowa wycena</NewValuationButton>}
                title={'Wycena Ceny Ofertowej Mieszkania przez AI'}
                customStepBackText="Nowa wycena"
            />
            <FiltersContentWrapper className="">
                <Container className="dark-mode-icons" id="filters">
                    <FiltersValuation />
                    {showPriceRange && <PriceRangeStep />}
                    {hasResults &&
                        <ResultContent>
                            <ValuationReportResult onExportPdf={handleDownloadPdf} isExportingPdf={isPrintMode} />
                            <ValuationAnalysis />
                        </ResultContent>
                    }
                </Container>
            </FiltersContentWrapper>
            <ResourcePopup
                isOpen={isOpenResourcePopUp}
                availableResource={{ smsCount: null, tokenCount: 0 }}
                onClose={closeResourcePopUp}
            />
        </Wrapper>
    );
};

export default PropertyValuationPage;
